import {
  listWireDetectors,
  detectWireProfile,
  getWireCapabilities,
  GRPC_WEB_CONNECT_CAPABILITIES,
  registerWireDetector,
  WIRE_FRAMING,
  WIRE_INTERACTION,
  WIRE_PROTOCOL_FAMILY,
  WIRE_SERIALIZATION,
  WIRE_TRANSPORT,
  type ProtocolCapabilities,
  type WireDetectInput,
  type WireDetector,
  type WireFraming,
  type WireHeaderList,
  type WireHeaderMap,
  type WireInteraction,
  type WireProfile,
} from './wire-profile';

export const GRPC_WEB_CONTENT_TYPE = 'application/grpc-web';
export const GRPC_WEB_PROTO_CONTENT_TYPE = 'application/grpc-web+proto';
export const GRPC_WEB_TEXT_CONTENT_TYPE = 'application/grpc-web-text';
export const GRPC_WEB_TEXT_PROTO_CONTENT_TYPE =
  'application/grpc-web-text+proto';
export const NATIVE_HTTP2_GRPC_CONTENT_TYPE = 'application/grpc';
export const NATIVE_HTTP2_GRPC_PROTO_CONTENT_TYPE = 'application/grpc+proto';
export const CONNECT_PROTO_CONTENT_TYPE = 'application/connect+proto';
export const CONNECT_UNARY_PROTO_CONTENT_TYPE = 'application/proto';

export const GRPC_WEB_CONNECT_CONTENT_TYPES = [
  GRPC_WEB_CONTENT_TYPE,
  GRPC_WEB_PROTO_CONTENT_TYPE,
  GRPC_WEB_TEXT_CONTENT_TYPE,
  GRPC_WEB_TEXT_PROTO_CONTENT_TYPE,
  CONNECT_PROTO_CONTENT_TYPE,
  CONNECT_UNARY_PROTO_CONTENT_TYPE,
] as const;

export type GrpcWebConnectContentType =
  (typeof GRPC_WEB_CONNECT_CONTENT_TYPES)[number];

const RPC_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;
const RPC_SERVICE_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_.]*$/;
const NATIVE_GRPC_CONTENT_TYPE = /^application\/grpc(?:\+|$)/i;

function normalizeMediaType(value: string | undefined): string {
  if (!value) {
    return '';
  }

  return value.split(';')[0].trim().toLowerCase();
}

function isHeaderList(
  headers: WireHeaderList | WireHeaderMap | undefined,
): headers is WireHeaderList {
  return Array.isArray(headers);
}

export function readWireHeader({
  headers,
  name,
}: {
  headers: WireHeaderList | WireHeaderMap | undefined;
  name: string;
}): string | undefined {
  if (!headers) {
    return undefined;
  }

  const target = name.toLowerCase();

  if (isHeaderList(headers)) {
    const match = headers.find(
      (header) => header.name?.trim().toLowerCase() === target,
    )?.value;
    const value = match?.trim();

    return value || undefined;
  }

  for (const [headerName, headerValue] of Object.entries(headers)) {
    if (headerName.toLowerCase() !== target) {
      continue;
    }

    const raw = Array.isArray(headerValue) ? headerValue[0] : headerValue;
    const value = raw?.trim();

    return value || undefined;
  }

  return undefined;
}

function contentTypeFromHeaders(
  headers: WireHeaderList | WireHeaderMap | undefined,
): string {
  return normalizeMediaType(readWireHeader({ headers, name: 'content-type' }));
}

export function isNativeHttp2GrpcContentType(
  contentType: string | undefined,
): boolean {
  const normalized = normalizeMediaType(contentType);

  if (!normalized) {
    return false;
  }

  if (normalized.includes('grpc-web')) {
    return false;
  }

  return NATIVE_GRPC_CONTENT_TYPE.test(normalized);
}

export function isGrpcWebTextContentType(
  contentType: string | undefined,
): boolean {
  return normalizeMediaType(contentType).includes('grpc-web-text');
}

export function isGrpcWebContentType(contentType: string | undefined): boolean {
  const normalized = normalizeMediaType(contentType);

  return (
    normalized.includes('grpc-web') && !normalized.includes('grpc-web-text')
  );
}

export function isConnectProtobufContentType(
  contentType: string | undefined,
): boolean {
  const normalized = normalizeMediaType(contentType);

  return (
    normalized === CONNECT_UNARY_PROTO_CONTENT_TYPE ||
    normalized === CONNECT_PROTO_CONTENT_TYPE ||
    normalized.startsWith('application/connect+proto')
  );
}

export function isGrpcWebOrConnectContentType(
  contentType: string | undefined,
): boolean {
  return (
    isGrpcWebContentType(contentType) ||
    isGrpcWebTextContentType(contentType) ||
    isConnectProtobufContentType(contentType)
  );
}

export function isBinaryGrpcWebOrConnectContentType(
  contentType: string | undefined,
): boolean {
  return (
    isGrpcWebOrConnectContentType(contentType) &&
    !isGrpcWebTextContentType(contentType)
  );
}

export function extractGrpcRpcIdentityFromUrl(
  url: string | undefined,
): { serviceName: string; methodName: string } | null {
  if (!url) {
    return null;
  }

  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/').filter(Boolean);

    if (segments.length < 2) {
      return null;
    }

    const methodName = segments[segments.length - 1];
    const serviceName = segments[segments.length - 2];

    if (!RPC_IDENTIFIER.test(methodName)) {
      return null;
    }

    if (!RPC_SERVICE_IDENTIFIER.test(serviceName)) {
      return null;
    }

    if (methodName.includes('.')) {
      return null;
    }

    return { serviceName, methodName };
  } catch {
    return null;
  }
}

export function isCanonicalGrpcRpcPath(path: string): boolean {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const segments = normalized.split('/').filter(Boolean);

  if (segments.length !== 2) {
    return false;
  }

  const identity = extractGrpcRpcIdentityFromUrl(
    `https://example.com${normalized}`,
  );

  return (
    identity?.serviceName === segments[0] && identity.methodName === segments[1]
  );
}

function framingFromContentType(contentType: string): WireFraming | null {
  if (isGrpcWebTextContentType(contentType)) {
    return WIRE_FRAMING.GRPC_WEB_TEXT;
  }

  if (isGrpcWebContentType(contentType)) {
    return WIRE_FRAMING.GRPC_WEB;
  }

  const normalized = normalizeMediaType(contentType);

  if (normalized === CONNECT_PROTO_CONTENT_TYPE) {
    return WIRE_FRAMING.CONNECT_STREAMING;
  }

  if (normalized === CONNECT_UNARY_PROTO_CONTENT_TYPE) {
    return WIRE_FRAMING.CONNECT;
  }

  return null;
}

function interactionFromFraming(framing: WireFraming): WireInteraction {
  if (framing === WIRE_FRAMING.CONNECT_STREAMING) {
    return WIRE_INTERACTION.SERVER_STREAMING;
  }

  return WIRE_INTERACTION.UNARY;
}

function familyFromFraming(framing: WireFraming): WireProfile['family'] {
  if (
    framing === WIRE_FRAMING.CONNECT ||
    framing === WIRE_FRAMING.CONNECT_STREAMING
  ) {
    return WIRE_PROTOCOL_FAMILY.CONNECT;
  }

  return WIRE_PROTOCOL_FAMILY.GRPC_WEB;
}

function resolveDeclaredContentType(input: WireDetectInput): string {
  const requestType = normalizeMediaType(
    input.requestContentType ?? contentTypeFromHeaders(input.requestHeaders),
  );
  const responseType = normalizeMediaType(
    input.responseContentType ?? contentTypeFromHeaders(input.responseHeaders),
  );

  if (isGrpcWebOrConnectContentType(requestType)) {
    return requestType;
  }

  if (isGrpcWebOrConnectContentType(responseType)) {
    return responseType;
  }

  return requestType || responseType;
}

function hasConnectProtocolHeader(input: WireDetectInput): boolean {
  return Boolean(
    readWireHeader({
      headers: input.requestHeaders,
      name: 'connect-protocol-version',
    }) ||
      readWireHeader({
        headers: input.responseHeaders,
        name: 'connect-protocol-version',
      }),
  );
}

function hasGrpcWebHeader(input: WireDetectInput): boolean {
  const names = [
    'x-grpc-web',
    'grpc-status',
    'grpc-message',
    'grpc-encoding',
    'grpc-accept-encoding',
  ];

  return names.some(
    (name) =>
      Boolean(readWireHeader({ headers: input.requestHeaders, name })) ||
      Boolean(readWireHeader({ headers: input.responseHeaders, name })),
  );
}

function detectGrpcWebOrConnectProfile(
  input: WireDetectInput,
): WireProfile | null {
  const requestType = normalizeMediaType(
    input.requestContentType ?? contentTypeFromHeaders(input.requestHeaders),
  );
  const responseType = normalizeMediaType(
    input.responseContentType ?? contentTypeFromHeaders(input.responseHeaders),
  );

  if (
    isNativeHttp2GrpcContentType(requestType) ||
    isNativeHttp2GrpcContentType(responseType)
  ) {
    return null;
  }

  const declaredType = resolveDeclaredContentType(input);
  let framing = framingFromContentType(declaredType);

  if (!framing && hasConnectProtocolHeader(input)) {
    framing = WIRE_FRAMING.CONNECT;
  }

  if (!framing && hasGrpcWebHeader(input)) {
    framing = WIRE_FRAMING.GRPC_WEB;
  }

  if (!framing) {
    return null;
  }

  const rpcIdentity = extractGrpcRpcIdentityFromUrl(input.url);
  const family = familyFromFraming(framing);

  return {
    family,
    transport: WIRE_TRANSPORT.HTTP,
    interaction: interactionFromFraming(framing),
    serialization: WIRE_SERIALIZATION.PROTOBUF,
    framing,
    ...(rpcIdentity?.serviceName
      ? { serviceName: rpcIdentity.serviceName }
      : {}),
    ...(rpcIdentity?.methodName ? { methodName: rpcIdentity.methodName } : {}),
    ...(declaredType ? { contentType: declaredType } : {}),
  };
}

const grpcWebDetector: WireDetector = {
  id: WIRE_PROTOCOL_FAMILY.GRPC_WEB,
  capabilities: GRPC_WEB_CONNECT_CAPABILITIES,
  detect: (input) => {
    const profile = detectGrpcWebOrConnectProfile(input);

    return profile?.family === WIRE_PROTOCOL_FAMILY.GRPC_WEB ? profile : null;
  },
};

const connectDetector: WireDetector = {
  id: WIRE_PROTOCOL_FAMILY.CONNECT,
  capabilities: GRPC_WEB_CONNECT_CAPABILITIES,
  detect: (input) => {
    const profile = detectGrpcWebOrConnectProfile(input);

    return profile?.family === WIRE_PROTOCOL_FAMILY.CONNECT ? profile : null;
  },
};

let defaultDetectorsRegistered = false;

export function ensureDefaultWireDetectors(): void {
  if (defaultDetectorsRegistered && listWireDetectors().length > 0) {
    return;
  }

  registerWireDetector(grpcWebDetector);
  registerWireDetector(connectDetector);
  defaultDetectorsRegistered = true;
}

ensureDefaultWireDetectors();

export function classifyGrpcWebOrConnect(input: WireDetectInput): {
  profile: WireProfile | null;
  grpcServiceName: string | null;
  grpcMethodName: string | null;
  capabilities: ProtocolCapabilities;
} {
  ensureDefaultWireDetectors();
  const profile = detectWireProfile(input);

  return {
    profile,
    grpcServiceName: profile?.serviceName ?? null,
    grpcMethodName: profile?.methodName ?? null,
    capabilities: getWireCapabilities(profile),
  };
}
